/**
 * Utilisation Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * "Utilised"  = confirmed / completed bookings.
 * "Planned"   = planned (unconfirmed) bookings.
 * No billable/non-billable split — billing is in the external PSA.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { format, startOfQuarter, endOfQuarter } from 'date-fns';
import {
  BarChart2,
  ChevronUp,
  ChevronDown,
  Printer,
  Download,
  AlertTriangle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useScheduler } from '@/contexts/SchedulerContext';
import {
  useUtilisationData,
  type UtilisationRow,
  type UtilisationType,
  type ViewBy,
} from './useUtilisationData';

// ─── Colour tokens ────────────────────────────────────────────────────────────

const TEAL = '#1D9E75';
const AMBER = '#EF9F27';
const RED = '#E24B4A';
const PLANNED_BLUE = '#B5D4F4';
const GREY_DASHED = '#9CA3AF';

// ─── Sub-components ───────────────────────────────────────────────────────────

// Segmented control
interface SegmentedProps<T extends string> {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center bg-muted rounded-md p-[3px] gap-[2px]">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            'px-2.5 h-7 rounded text-xs font-medium transition-all whitespace-nowrap',
            o.value === value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Sortable column header
interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        <span className={active ? 'text-foreground' : 'opacity-0 group-hover:opacity-50'}>
          {active && sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </span>
      </span>
    </th>
  );
}

// Pct cell with colour coding
function PctCell({ pct, targetPct }: { pct: number; targetPct: number }) {
  let colour = 'text-foreground';
  if (pct > 100) colour = 'text-[#E24B4A]';
  else if (pct >= targetPct) colour = 'text-[#1D9E75]';
  else if (pct >= targetPct - 10) colour = 'text-[#EF9F27]';
  else colour = 'text-[#E24B4A]';
  return <span className={`${colour} font-medium`}>{pct}%</span>;
}

// Variance cell
function VarianceCell({ variance }: { variance: number }) {
  const colour = variance >= 0 ? 'text-[#1D9E75]' : 'text-[#E24B4A]';
  const prefix = variance >= 0 ? '+' : '';
  return <span className={colour}>{prefix}{variance}%</span>;
}

// Status pill
function StatusPill({ status }: { status: UtilisationRow['status'] }) {
  switch (status) {
    case 'on-target':
      return <Badge variant="success" accent="success" className="text-[11px] px-2 py-0.5">On target</Badge>;
    case 'below-target':
      return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5">Below target</Badge>;
    case 'overloaded':
      return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5">Overloaded</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">No bookings</Badge>;
  }
}

// Booking status pill (inside drawer)
function BookingStatusPill({ status }: { status: 'confirmed' | 'planned' }) {
  if (status === 'confirmed') {
    return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 capitalize">Confirmed</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground capitalize">Planned</Badge>;
}

// Summary stat card
interface StatCardProps {
  value: string;
  valueColour?: string;
  label: string;
  subLabel: string;
}
function StatCard({ value, valueColour, label, subLabel }: StatCardProps) {
  return (
    <div
      className="flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] p-4 min-w-0"
      style={{
        background: 'var(--color-background-secondary, var(--muted))',
        border: '0.5px solid var(--color-border-tertiary, var(--border))',
      }}
    >
      <span
        className="text-2xl font-medium leading-none"
        style={{ color: valueColour ?? 'var(--foreground)' }}
      >
        {value}
      </span>
      <span className="text-[12px] text-muted-foreground mt-1">{label}</span>
      <span className="text-[11px] text-muted-foreground">{subLabel}</span>
    </div>
  );
}

// Chart legend swatch
function LegendSwatch({ colour, label, dashed }: { colour: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      {dashed ? (
        <svg width="16" height="4" className="shrink-0">
          <line x1="0" y1="2" x2="16" y2="2" stroke={colour} strokeWidth="1.5" strokeDasharray="3 2" />
        </svg>
      ) : (
        <span className="inline-block h-3 w-3 rounded-[2px] shrink-0" style={{ background: colour }} />
      )}
      {label}
    </span>
  );
}

// Custom chart tooltip
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { target: number } }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const target = payload[0]?.payload?.target ?? 75;
  const utilised = payload.find((p) => p.name === 'utilised')?.value ?? 0;
  const planned = payload.find((p) => p.name === 'planned')?.value ?? 0;
  const variance = utilised - target;
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1 min-w-[170px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p>Utilised: <strong>{utilised}%</strong></p>
      <p>Planned: <strong>{planned}%</strong></p>
      <p>Target: <strong>{target}%</strong></p>
      <p className={variance >= 0 ? 'text-[#1D9E75]' : 'text-[#E24B4A]'}>
        Variance: <strong>{variance >= 0 ? '+' : ''}{variance}%</strong>
      </p>
    </div>
  );
}

// ─── Resource detail drawer ───────────────────────────────────────────────────

function ResourceDrawer({
  row,
  open,
  onClose,
  onNavigateToHeatmap,
}: {
  row: UtilisationRow | null;
  open: boolean;
  onClose: () => void;
  onNavigateToHeatmap?: () => void;
}) {
  if (!row) return null;

  const sparkData = row.weeklyUtilisation.map((v, i) => ({ w: i, pct: v }));

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden"
        style={{ width: 400, maxWidth: '100vw' }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '0.5px solid var(--border)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarImage src={row.photoUrl} alt={row.name} />
              <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-[14px] text-foreground truncate">{row.name}</p>
              <p className="text-[12px] text-muted-foreground truncate">{row.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <div className="text-right">
              <p
                className="text-[24px] font-medium leading-none"
                style={{ color: row.utilisedColour }}
              >
                {row.utilisedPct}%
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Utilised</p>
            </div>
            <StatusPill status={row.status} />
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Section 1 — Utilisation breakdown */}
          <div className="px-5 py-4" style={{ borderBottom: '0.5px solid var(--border)' }}>
            <p className="text-[12px] font-medium text-foreground mb-3">Utilisation breakdown</p>

            <div className="flex flex-col gap-3">
              {/* Utilised bar */}
              <div>
                <div className="flex justify-between text-[12px] mb-1">
                  <span className="text-muted-foreground">Utilised (confirmed)</span>
                  <span className="font-medium" style={{ color: row.utilisedColour }}>
                    {row.utilisedPct}%
                  </span>
                </div>
                <div className="relative">
                  <Progress
                    value={Math.min(row.utilisedPct, 100)}
                    className="h-2"
                    style={
                      {
                        '--progress-color': row.utilisedColour,
                        background: 'var(--muted)',
                      } as React.CSSProperties
                    }
                  />
                  {/* Target marker */}
                  <div
                    className="absolute top-0 bottom-0 w-[2px] rounded"
                    style={{
                      left: `${Math.min(row.targetPct, 100)}%`,
                      background: TEAL,
                      opacity: 0.6,
                    }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Target: {row.targetPct}%
                </p>
              </div>

              {/* Planned bar */}
              <div>
                <div className="flex justify-between text-[12px] mb-1">
                  <span className="text-muted-foreground">Planned (confirmed + planned)</span>
                  <span className="font-medium text-foreground">{row.plannedPct}%</span>
                </div>
                <Progress
                  value={Math.min(row.plannedPct, 100)}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>

              {/* Stat chips */}
              <div className="flex gap-2 mt-1">
                {[
                  { label: 'Confirmed', value: `${row.utilisedHours}h` },
                  { label: 'Planned', value: `${row.plannedHours}h` },
                  { label: 'Available', value: `${row.availableHours}h` },
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center"
                    style={{
                      background: 'var(--muted)',
                      border: '0.5px solid var(--border)',
                    }}
                  >
                    <span className="text-[13px] font-medium text-foreground">{chip.value}</span>
                    <span className="text-[10px] text-muted-foreground">{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2 — Booking breakdown */}
          <div className="px-5 py-4" style={{ borderBottom: '0.5px solid var(--border)' }}>
            <p className="text-[12px] font-medium text-foreground mb-3">Booking breakdown</p>
            {row.periodBookings.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">No bookings in this period.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {row.periodBookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-start justify-between gap-2 rounded-md px-3 py-2 cursor-pointer hover:bg-accent/40 transition-colors"
                    style={{ border: '0.5px solid var(--border)' }}
                  >
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium text-foreground truncate">{b.projectName}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {b.startDate} – {b.endDate}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{b.allocationPct}% allocation</p>
                    </div>
                    <BookingStatusPill status={b.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3 — Trend sparkline */}
          <div className="px-5 py-4" style={{ borderBottom: '0.5px solid var(--border)' }}>
            <p className="text-[12px] font-medium text-foreground mb-3">Trend — last 12 weeks</p>
            <div className="h-16 w-full overflow-hidden">
              <LineChart
                width={340}
                height={64}
                data={sparkData}
                margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
              >
                <ReferenceLine y={row.targetPct} stroke={TEAL} strokeDasharray="3 2" strokeWidth={1} />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke={TEAL}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Dashed line = target ({row.targetPct}%)
            </p>
          </div>

          {/* Section 4 — Burnout risk */}
          {row.hasBurnoutFlag && (
            <div className="px-5 py-4">
              <p className="text-[12px] font-medium text-foreground mb-3">Burnout risk</p>
              <div
                className="rounded-md px-3 py-2.5 flex items-start gap-2"
                style={{
                  background: 'var(--color-warning-50, #fff7ed)',
                  border: '0.5px solid var(--color-warning-200, #fde68a)',
                }}
              >
                <AlertTriangle className="h-4 w-4 text-[#EF9F27] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[12px] text-foreground">
                    {row.name} is trending toward <strong>{row.burnoutPct}%</strong> utilisation
                    {row.burnoutWeek ? ` in ${row.burnoutWeek}` : ''}.
                  </p>
                  {onNavigateToHeatmap && (
                    <button
                      onClick={onNavigateToHeatmap}
                      className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
                    >
                      View in heatmap
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <BarChart2 className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No utilisation data for the selected period
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        Try adjusting the filters or date range.
      </p>
      <button
        onClick={onReset}
        className="text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        Reset filters
      </button>
    </div>
  );
}

// ─── Date range display ───────────────────────────────────────────────────────

function formatDateRange(from: Date, to: Date): string {
  return `${format(from, 'dd MMM yyyy')} — ${format(to, 'dd MMM yyyy')}`;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function UtilisationReport() {
  const { resources } = useScheduler();

  // ── Defaults
  const defaultFrom = startOfQuarter(new Date());
  const defaultTo = endOfQuarter(new Date());

  // ── Filter state
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo] = useState<Date>(defaultTo);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'rolling12'>('month');
  const [viewBy, setViewBy] = useState<ViewBy>('individual');
  const [utilType, setUtilType] = useState<UtilisationType>('utilised');
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  // ── Sort state
  const [sortCol, setSortCol] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // ── Drawer
  const [drawerRow, setDrawerRow] = useState<UtilisationRow | null>(null);

  // ── Chart controls (independent of table sort/pagination)
  const [chartShowLimit, setChartShowLimit] = useState<number>(25);
  const [chartSortBy, setChartSortBy] = useState<
    'name' | 'utilised-desc' | 'utilised-asc' | 'planned-desc' | 'variance-desc'
  >('utilised-desc');

  // ── Chart container measurement (responsive bar sizing)
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const [chartContainerWidth, setChartContainerWidth] = useState<number>(0);

  // ── Data
  const data = useUtilisationData(dateFrom, dateTo, viewBy, selectedResources, selectedDepartments);

  // ── Observe chart container width so the chart fills available space
  useEffect(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setChartContainerWidth(w);
    });
    ro.observe(el);
    // Seed with current width immediately (before first resize fires)
    setChartContainerWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // ── Resource options for multi-select
  const resourceOptions = useMemo(
    () =>
      resources
        .filter((r) => r.id !== 'unallocated')
        .map((r) => ({ value: r.id, label: r.name })),
    [resources],
  );

  // ── Department options
  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const dept = r.schedulerGroup ?? r.department ?? '';
        if (dept && !seen.has(dept)) {
          seen.add(dept);
          acc.push({ value: dept, label: dept });
        }
        return acc;
      }, []);
  }, [resources]);

  // ── Chart display data — sorted + sliced independently of the table
  const chartDisplayData = useMemo(() => {
    const sorted = [...data.chartData].sort((a, b) => {
      switch (chartSortBy) {
        case 'utilised-desc': return b.utilised - a.utilised;
        case 'utilised-asc':  return a.utilised - b.utilised;
        case 'planned-desc':  return b.planned  - a.planned;
        case 'variance-desc': return (b.utilised - b.target) - (a.utilised - a.target);
        case 'name':
        default:              return a.name.localeCompare(b.name);
      }
    });
    return chartShowLimit === -1 ? sorted : sorted.slice(0, chartShowLimit);
  }, [data.chartData, chartSortBy, chartShowLimit]);

  // ── Responsive chart sizing
  // Calculate how wide each resource group should be so bars fill the container.
  // Minimum 36px per group (readable), maximum 96px (not too wide when few resources).
  // If all groups at minimum still exceed the container, horizontal scroll kicks in.
  const Y_AXIS_WIDTH = 44;
  const MIN_GROUP_WIDTH = 36;
  const MAX_GROUP_WIDTH = 96;
  const resourceCount = chartDisplayData.length || 1;
  const availableForBars = Math.max(0, chartContainerWidth - Y_AXIS_WIDTH);
  const idealGroupWidth = availableForBars / resourceCount;
  const groupWidth = Math.min(MAX_GROUP_WIDTH, Math.max(MIN_GROUP_WIDTH, idealGroupWidth));
  // Inner canvas width: either fills the container exactly, or is wider (scroll)
  const chartInnerWidth = Math.max(
    chartContainerWidth || 560,
    resourceCount * groupWidth + Y_AXIS_WIDTH,
  );

  // ── Sort + paginate rows
  const sortedRows = useMemo(() => {
    return [...data.rows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'name':       va = a.name;           vb = b.name;           break;
        case 'department': va = a.department;     vb = b.department;     break;
        case 'target':     va = a.targetPct;      vb = b.targetPct;      break;
        case 'utilised':   va = a.utilisedPct;    vb = b.utilisedPct;    break;
        case 'planned':    va = a.plannedPct;     vb = b.plannedPct;     break;
        case 'variance':   va = a.variancePct;    vb = b.variancePct;    break;
        case 'confirmed':  va = a.utilisedHours;  vb = b.utilisedHours;  break;
        case 'plannedh':   va = a.plannedHours;   vb = b.plannedHours;   break;
        case 'available':  va = a.availableHours; vb = b.availableHours; break;
        default:           va = a.name;           vb = b.name;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc'
          ? va.localeCompare(vb as string)
          : (vb as string).localeCompare(va);
      }
      return sortDir === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });
  }, [data.rows, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = useCallback(
    (col: string) => {
      if (col === sortCol) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
      setPage(1);
    },
    [sortCol],
  );

  const handleReset = useCallback(() => {
    setDateFrom(defaultFrom);
    setDateTo(defaultTo);
    setPeriod('month');
    setViewBy('individual');
    setUtilType('utilised');
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSortCol('name');
    setSortDir('asc');
    setPage(1);
    setChartShowLimit(25);
    setChartSortBy('utilised-desc');
  }, [defaultFrom, defaultTo]);

  // Planned utilisation colour for summary card
  const plannedColour = useMemo(() => {
    const { avgPlanned } = data.summary;
    const target = 75;
    if (avgPlanned > 100) return RED;
    if (avgPlanned >= target) return TEAL;
    if (avgPlanned >= target - 10) return AMBER;
    return RED;
  }, [data.summary]);

  // Footer totals
  const footerTotals = useMemo(() => {
    const rows = data.rows;
    if (rows.length === 0) return null;
    const activeRows = rows.filter((r) => r.utilisedHours > 0 || r.plannedHours > 0);
    return {
      targetAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.targetPct, 0) / activeRows.length)
          : 0,
      utilisedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.utilisedPct, 0) / activeRows.length)
          : 0,
      plannedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.plannedPct, 0) / activeRows.length)
          : 0,
      varianceAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.variancePct, 0) / activeRows.length)
          : 0,
      confirmedSum: rows.reduce((s, r) => s + r.utilisedHours, 0),
      plannedSum: rows.reduce((s, r) => s + r.plannedHours, 0),
      availableSum: rows.reduce((s, r) => s + r.availableHours, 0),
    };
  }, [data.rows]);

  // Chart bar colours based on utilType filter
  function barFillUtilised(entry: { utilised: number; target: number }) {
    const base =
      entry.utilised > 100
        ? RED
        : entry.utilised >= entry.target
        ? TEAL
        : entry.utilised >= entry.target - 10
        ? AMBER
        : RED;
    return utilType === 'planned' ? `${base}66` : base; // dim to 40% when filter is "Planned only"
  }
  function barFillPlanned() {
    return utilType === 'utilised' ? `${PLANNED_BLUE}66` : PLANNED_BLUE;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={{ borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex items-center justify-center h-8 w-8 rounded-md shrink-0"
            style={{ background: 'rgba(29,158,117,0.1)' }}
          >
            <BarChart2 className="h-4 w-4" style={{ color: TEAL }} />
          </div>
          <div className="min-w-0">
            <Breadcrumb
              items={[{ label: 'Reports' }, { label: 'Utilisation report' }]}
              className="text-[12px] text-muted-foreground mb-0.5"
            />
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Utilisation report
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Booking-based utilisation — confirmed and planned bookings
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <Button variant="outline" size="sm" className="gap-1.5 text-[13px]">
            <Download className="h-3.5 w-3.5" />
            Export to CSV
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8">
            <Printer className="h-3.5 w-3.5" />
            <span className="sr-only">Print</span>
          </Button>
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 flex-wrap px-4 py-3 shrink-0"
        style={{
          background: 'var(--color-background-secondary, var(--muted))',
          borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))',
        }}
      >
        {/* Date range display */}
        <div
          className="inline-flex items-center h-8 px-3 rounded-md border bg-background text-[13px] text-foreground cursor-pointer hover:bg-accent/40 transition-colors shrink-0"
          style={{ border: '1px solid var(--input)' }}
          title="Date range"
        >
          {formatDateRange(dateFrom, dateTo)}
        </div>

        {/* Period */}
        <Segmented
          options={[
            { label: 'Week', value: 'week' },
            { label: 'Month', value: 'month' },
            { label: 'Quarter', value: 'quarter' },
            { label: 'Rolling 12m', value: 'rolling12' },
          ]}
          value={period}
          onChange={(v) => { setPeriod(v); setPage(1); }}
        />

        {/* View by */}
        <Select value={viewBy} onValueChange={(v) => { setViewBy(v as ViewBy); setPage(1); setSortCol('name'); setSortDir('asc'); }}>
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[130px]">
            <SelectValue placeholder="View by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="team">Team</SelectItem>
            <SelectItem value="department">Department</SelectItem>
            <SelectItem value="location">Location</SelectItem>
          </SelectContent>
        </Select>

        {/* Utilisation type */}
        <Segmented
          options={[
            { label: 'Utilised', value: 'utilised' },
            { label: 'Planned', value: 'planned' },
            { label: 'Both', value: 'both' },
          ]}
          value={utilType}
          onChange={(v) => setUtilType(v as UtilisationType)}
        />

        {/* Resource multi-select */}
        <div className="w-44 shrink-0">
          <MultiSelect
            options={resourceOptions}
            placeholder="All resources"
            value={selectedResources}
            onValueChange={(v) => { setSelectedResources(v); setPage(1); }}
          />
        </div>

        {/* Department multi-select */}
        <div className="w-44 shrink-0">
          <MultiSelect
            options={departmentOptions}
            placeholder="All departments"
            value={selectedDepartments}
            onValueChange={(v) => { setSelectedDepartments(v); setPage(1); }}
          />
        </div>

        {/* Reset */}
        <button
          onClick={handleReset}
          className="ml-auto text-[13px] hover:underline cursor-pointer shrink-0"
          style={{ color: 'var(--color-text-info, var(--primary))' }}
        >
          Reset filters
        </button>
      </div>

      {/* ── Scrollable content area ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
        {data.isEmpty ? (
          <EmptyState onReset={handleReset} />
        ) : (
          <>
            {/* ── Stat cards ────────────────────────────────────────────── */}
            <div className="flex gap-3">
              <StatCard
                value={`${data.summary.avgUtilised}%`}
                valueColour={TEAL}
                label="Avg utilised"
                subLabel="Confirmed bookings · current period"
              />
              <StatCard
                value={`${data.summary.avgPlanned}%`}
                valueColour={plannedColour}
                label="Avg planned utilisation"
                subLabel="Confirmed + planned bookings"
              />
              <StatCard
                value={`${data.summary.totalUtilisedHours}h`}
                label="Total confirmed hours"
                subLabel="Confirmed bookings only"
              />
              <StatCard
                value={`${data.summary.resourcesAtTarget} of ${data.summary.totalResources}`}
                label="Resources at target"
                subLabel="Meeting or exceeding utilisation target"
              />
            </div>

            {/* ── Grouped bar chart ────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              {/* Chart toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[12px] font-medium text-foreground">
                    {chartDisplayData.length} of {data.chartData.length} resources
                  </span>
                  {data.chartData.length > chartDisplayData.length && (
                    <span className="text-[11px] text-muted-foreground">
                      · filtered to top {chartDisplayData.length} · full data in table below
                    </span>
                  )}
                  {chartInnerWidth > chartContainerWidth && chartContainerWidth > 0 && data.chartData.length === chartDisplayData.length && (
                    <span className="text-[11px] text-muted-foreground">
                      · scroll chart right to see all
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {/* Sort by */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sort by</span>
                    <Select
                      value={chartSortBy}
                      onValueChange={(v) =>
                        setChartSortBy(
                          v as 'name' | 'utilised-desc' | 'utilised-asc' | 'planned-desc' | 'variance-desc',
                        )
                      }
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utilised-desc">Utilised % (high → low)</SelectItem>
                        <SelectItem value="utilised-asc">Utilised % (low → high)</SelectItem>
                        <SelectItem value="planned-desc">Planned % (high → low)</SelectItem>
                        <SelectItem value="variance-desc">Variance (worst first)</SelectItem>
                        <SelectItem value="name">Name (A–Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show limit */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Show</span>
                    <Select
                      value={String(chartShowLimit)}
                      onValueChange={(v) => setChartShowLimit(Number(v))}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="-1">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Scrollable chart area — measured for responsive sizing, scrolls when bars exceed width */}
              <div className="relative" ref={chartScrollRef}>
                {/* Right-edge fade — only shown when chart is wider than the container (scroll active) */}
                {chartInnerWidth > chartContainerWidth && chartContainerWidth > 0 && (
                  <div
                    className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 z-10 rounded-r-sm"
                    style={{
                      background: 'linear-gradient(to right, transparent, var(--background))',
                    }}
                  />
                )}

                <div
                  className="overflow-x-auto"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {/* Fixed-width inner div — recharts renders at exactly this width */}
                  <div style={{ width: chartInnerWidth, height: 280 }}>
                    <BarChart
                      width={chartInnerWidth}
                      height={280}
                      data={chartDisplayData}
                      margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                      barCategoryGap="35%"
                      barGap={2}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        tickFormatter={(name: string) =>
                          name.length > 8 ? `${name.slice(0, 7)}…` : name
                        }
                      />
                      <YAxis
                        domain={[0, 120]}
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                        width={Y_AXIS_WIDTH}
                      />
                      <Tooltip
                        content={<ChartTooltip />}
                        cursor={{ fill: 'var(--accent)', opacity: 0.3 }}
                      />
                      <ReferenceLine
                        y={100}
                        stroke={GREY_DASHED}
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        label={{
                          value: 'Capacity limit',
                          position: 'insideTopRight',
                          fontSize: 10,
                          fill: GREY_DASHED,
                          dy: -6,
                        }}
                      />
                      <ReferenceLine
                        y={75}
                        stroke={TEAL}
                        strokeDasharray="4 3"
                        strokeWidth={1.5}
                        label={{
                          value: 'Target',
                          position: 'insideTopRight',
                          fontSize: 10,
                          fill: TEAL,
                          dy: -6,
                        }}
                      />
                      <Bar dataKey="utilised" name="utilised" radius={[3, 3, 0, 0]} maxBarSize={22}>
                        {chartDisplayData.map((entry, idx) => (
                          <Cell key={idx} fill={barFillUtilised(entry)} />
                        ))}
                      </Bar>
                      <Bar
                        dataKey="planned"
                        name="planned"
                        radius={[3, 3, 0, 0]}
                        fill={barFillPlanned()}
                        maxBarSize={22}
                      />
                    </BarChart>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-1">
                <LegendSwatch colour={TEAL}        label="Utilised (confirmed)" />
                <LegendSwatch colour={PLANNED_BLUE} label="Planned" />
                <LegendSwatch colour={TEAL}        label="At target" />
                <LegendSwatch colour={AMBER}       label="Below target" />
                <LegendSwatch colour={RED}         label="Overloaded" />
                <LegendSwatch colour={TEAL}        label="Target line"      dashed />
                <LegendSwatch colour={GREY_DASHED} label="Capacity limit"   dashed />
              </div>
            </div>

            {/* ── Data table ───────────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] overflow-hidden"
              style={{ border: '0.5px solid var(--color-border-tertiary, var(--border))' }}
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr style={{ background: 'var(--color-background-secondary, var(--muted))' }}>
                      <SortHeader label={viewBy === 'individual' ? 'Resource' : viewBy === 'team' ? 'Team' : viewBy === 'department' ? 'Department' : 'Location'} col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={200} />
                      <SortHeader label="Department" col="department" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                      <SortHeader label="Target %"   col="target"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Utilised %" col="utilised"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Planned %"  col="planned"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Variance"   col="variance"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Confirmed"  col="confirmed"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Planned hrs" col="plannedh"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Available"  col="available"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                      <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr
                        key={row.id}
                        onClick={() => setDrawerRow(row)}
                        className="cursor-pointer transition-colors"
                        style={{
                          height: 48,
                          background:
                            idx % 2 === 0
                              ? 'var(--background)'
                              : 'var(--color-background-secondary, var(--muted))',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = '#E1F5EE';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            idx % 2 === 0
                              ? 'var(--background)'
                              : 'var(--color-background-secondary, var(--muted))';
                        }}
                      >
                        {/* Resource */}
                        <td className="px-3 py-2" style={{ minWidth: 200 }}>
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={row.photoUrl} alt={row.name} />
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-[13px] text-foreground truncate leading-tight">
                                {row.name}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                                {row.role}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-foreground whitespace-nowrap">
                          {row.department}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">{row.targetPct}%</td>
                        <td className="px-3 py-2 text-right">
                          <PctCell pct={row.utilisedPct} targetPct={row.targetPct} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <PctCell pct={row.plannedPct} targetPct={row.targetPct} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <VarianceCell variance={row.variancePct} />
                        </td>
                        <td className="px-3 py-2 text-right text-foreground">{row.utilisedHours}h</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{row.plannedHours}h</td>
                        <td className="px-3 py-2 text-right text-foreground">{row.availableHours}h</td>
                        <td className="px-3 py-2">
                          <StatusPill status={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  {/* Footer totals row */}
                  {footerTotals && (
                    <tfoot>
                      <tr
                        style={{
                          background: 'var(--color-background-secondary, var(--muted))',
                          borderTop: '0.5px solid var(--color-border-primary, var(--border))',
                        }}
                      >
                        <td
                          className="px-3 py-2.5 font-medium text-[13px] text-foreground"
                          colSpan={2}
                        >
                          Team total / average
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.targetAvg}%
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <PctCell pct={footerTotals.utilisedAvg} targetPct={footerTotals.targetAvg} />
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <PctCell pct={footerTotals.plannedAvg} targetPct={footerTotals.targetAvg} />
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <VarianceCell variance={footerTotals.varianceAvg} />
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.confirmedSum}h
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-muted-foreground">
                          {footerTotals.plannedSum}h
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.availableSum}h
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Pagination ────────────────────────────────────────────── */}
            <div className="flex items-center justify-between shrink-0 pb-2">
              <p className="text-[13px] text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, sortedRows.length)}–
                {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length} resources
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-[13px]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-[13px] text-muted-foreground px-1">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-[13px]"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-20 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Resource detail drawer ────────────────────────────────────────── */}
      <ResourceDrawer
        row={drawerRow}
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
      />
    </div>
  );
}
